/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useEditor, useNode } from '@craftjs/core';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Input as CommonInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { AppTooltip } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppTooltip';
import { shouldHideContainerByChildren } from '@lmt/unicorn-shared/app/components/editor/components/blocks/containerVisibility';
import {
  UIEditorBlockWidth,
  WidthSelectControl,
} from '@lmt/unicorn-shared/app/components/editor/components/blocks/WidthSelectControl';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import { InputValuesContext } from '../../UIEditorTypes';
import { CheckboxSettings } from './CheckboxSettings';

import styles from './UIEditorBlocks.module.scss';

export interface ContainerProps {
  isBorder: boolean;
  isHiddenWhenEmpty: boolean;
  children?: ReactNode;
  padding: number;
  title?: string;
  size: UIEditorBlockWidth;
}

export const Container = (props: Partial<ContainerProps>) => {
  const { connectors, childNodeIds } = useNode((node) => ({
    childNodeIds: node.data.nodes as string[],
  }));
  const { connect, drag } = connectors!;
  const { isBorder, padding, children, isHiddenWhenEmpty } = {
    ...ContainerDefaultProps,
    ...props,
  };

  const { enabled, query } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const i18n = useTranslation();
  const dataBlockTypeName = useContextSelector(
    InputValuesContext,
    (v) => v.dataBlockTypeName,
  );

  const values = useContextSelector(InputValuesContext, (v) => v.values);
  const sharedValues = useContextSelector(
    InputValuesContext,
    (v) => v.sharedValues,
  );
  const isOnlyView = useContextSelector(
    InputValuesContext,
    (v) => v.isOnlyView,
  );

  const shouldHideContainer = shouldHideContainerByChildren(
    childNodeIds ?? [],
    query,
    {
      values,
      sharedValues,
      isOnlyView,
      enabled,
    },
    isHiddenWhenEmpty,
  );

  if (shouldHideContainer) {
    return null;
  }

  return (
    <div
      className={styles.containerBlock}
      ref={(ref) => connect(drag(ref as any))}
      data-border={enabled || isBorder}
      style={{
        padding: `${enabled || isBorder ? padding + 'px' : undefined}`,
        width: `calc(${props.size} - 10px)` /* we need to subtract margins from the size, otherwise 3 Containers of 1/3 width do not fit in one line (#29132)*/,
      }}
    >
      {props.title && props.isBorder ? (
        <div className={styles.containerTitle}>
          {i18n.t(
            'data-block-fields:' +
              dataBlockTypeName +
              '.group_title_block.' +
              props.title.toLowerCase(),
            props.title,
          )}
        </div>
      ) : null}
      {children}
    </div>
  );
};

export const ContainerSettings = () => {
  const i18next = useScopedTranslation('editor.settings');
  const node = useNode((node) => ({
    props: node.data.props,
  }));
  const props = node.props as ContainerProps;

  const { setProp } = node.actions;

  return (
    <div className={styles.settings}>
      <CheckboxSettings
        label={i18next.t('has_border')}
        isChecked={props.isBorder}
        setValue={(newValue) =>
          setProp((props: ContainerProps) => (props.isBorder = newValue))
        }
      />
      <AppTooltip title={i18next.t('hide_when_all_empty_tooltip')}>
        <div>
          <CheckboxSettings
            label={i18next.t('hide_when_all_empty')}
            isChecked={props.isHiddenWhenEmpty}
            setValue={(newValue) =>
              setProp(
                (props: ContainerProps) => (props.isHiddenWhenEmpty = newValue),
              )
            }
          />
        </div>
      </AppTooltip>
      <Field title={i18next.t('container_title_label')}>
        <CommonInput
          value={props.title}
          testId={'uieditor-container-title'}
          onChange={(e) => {
            setProp((props: ContainerProps) => (props.title = e.target?.value));
          }}
        />
      </Field>
      <WidthSelectControl props={props} setProp={setProp} />
    </div>
  );
};

export const ContainerDefaultProps: ContainerProps = {
  padding: 10,
  isBorder: false,
  isHiddenWhenEmpty: false,
  title: undefined,
  size: UIEditorBlockWidth.Stretch,
};

Container.craft = {
  defaultProps: ContainerDefaultProps,
  related: {
    settings: ContainerSettings,
  },
};
